/**
 * Copyright 2026 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { GetEventsViewQuery } from "~/generated/graphql";
import { EventEntity } from "../../types";
import {
  ApprovalBadgeKind,
  ApprovalBadgeMeta,
  getApprovalBadgeMeta,
  isApprovalRequiredAction,
  isApprovalRequiredActionBatch,
} from "../ApprovalBadge";

type EventsViewEvent = NonNullable<
  GetEventsViewQuery["eventsView"]["result"]
>[number];

type Props = {
  event: EventsViewEvent;
  sortedEventEntities: EventEntity[];
};

export function useEventApproval({ event, sortedEventEntities }: Props) {
  const approveActions = useMemo(
    () => event.actions?.filter(isApprovalRequiredAction),
    [event.actions]
  );

  const approveBatches = useMemo(
    () => event.actionBatches?.filter(isApprovalRequiredActionBatch),
    [event.actionBatches]
  );

  const pendingApprovalLength = useMemo(
    () => (approveActions?.length ?? 0) + (approveBatches?.length ?? 0),
    [approveActions, approveBatches]
  );

  const hasPendingApproval = pendingApprovalLength > 0;

  const entitiesApprovalBadgeMeta = useMemo(() => {
    const approvalRequiredEntities = sortedEventEntities.filter(
      (entity) =>
        isApprovalRequiredAction(entity) ||
        isApprovalRequiredActionBatch(entity)
    );

    return approvalRequiredEntities.reduce<Record<string, ApprovalBadgeMeta>>(
      (acc, entity) => {
        acc[entity.id] = getApprovalBadgeMeta(entity);

        return acc;
      },
      {}
    );
  }, [sortedEventEntities]);

  const eventApprovalBadgeMeta = useMemo(() => {
    const approvalMetaValues = Object.values(entitiesApprovalBadgeMeta);

    return (
      approvalMetaValues.find(
        (meta) => meta.kind === ApprovalBadgeKind.leftTime
      ) ?? approvalMetaValues[0]
    );
  }, [entitiesApprovalBadgeMeta]);

  return {
    approveActions,
    approveBatches,
    pendingApprovalLength,
    hasPendingApproval,
    entitiesApprovalBadgeMeta,
    eventApprovalBadgeMeta,
  } as const;
}
